import { FilterMatchMode, FilterOperator } from 'primereact/api';

export const matchModeToOperator = {
  [FilterMatchMode.CONTAINS]: 1,
  [FilterMatchMode.NOT_CONTAINS]: 2,
  [FilterMatchMode.STARTS_WITH]: 3,
  [FilterMatchMode.ENDS_WITH]: 4,
  [FilterMatchMode.EQUALS]: 5,
  [FilterMatchMode.NOT_EQUALS]: 6,
  [FilterMatchMode.DATE_IS]: 5,
  [FilterMatchMode.DATE_IS_NOT]: 6,
  [FilterMatchMode.DATE_AFTER]: 7,
  [FilterMatchMode.DATE_BEFORE]: 8,
  [FilterMatchMode.GREATER_THAN]: 7,
  [FilterMatchMode.LESS_THAN]: 8,
  [FilterMatchMode.GREATER_THAN_OR_EQUAL_TO]: 9,
  [FilterMatchMode.LESS_THAN_OR_EQUAL_TO]: 10
};

/**
 * GenericTableFilter'dan gelen filtreleri PrimeReact DataTable filtre formatına dönüştürür
 * 
 * @param {Object} updatedFilters - GenericTableFilter'dan gelen filtre değerleri
 * @param {Object} currentFilters - Mevcut PrimeReact DataTable filtreleri
 * @param {Object} filterTypeMap - Filtre alanları ve tipleri/matchMode eşleştirmesi 
 *                               (ör: { markaAdi: 'autoComplete' } veya { markaAdi: { type: 'autoComplete', matchMode: FilterMatchMode.EQUALS } })
 * @returns {Object} - PrimeReact DataTable için filtre nesnesi
 */
export const convertFiltersToPrimeReactFormat = (updatedFilters, currentFilters, filterTypeMap = {}) => {
  const _filters = { ...currentFilters };
  
  Object.entries(updatedFilters).forEach(([key, value]) => {
    // Filtre boş ise, değeri null olarak ayarla
    if (value === '' || value === undefined || (Array.isArray(value) && value.length === 0)) {
      // Filtre tipi haritasından bu alanın tipini ve matchMode'unu al
      const fieldConfig = filterTypeMap[key] || { type: 'text', matchMode: FilterMatchMode.STARTS_WITH };
      const filterType = typeof fieldConfig === 'object' ? fieldConfig.type : fieldConfig;
      const matchMode = typeof fieldConfig === 'object' ? fieldConfig.matchMode : 
        (filterType === 'text' ? FilterMatchMode.STARTS_WITH : FilterMatchMode.EQUALS);
      
      // Filtre tipine göre doğru boş değer yapısını oluştur
      switch (filterType) {
        case 'text':
        default:
          _filters[key] = {
            operator: FilterOperator.AND,
            constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
          };
          break;
        case 'number':
        case 'date':
          _filters[key] = {
            operator: FilterOperator.AND,
            constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }]
          };
          break;
        case 'boolean':
        case 'autoComplete':
        case 'dropdown':
          _filters[key] = { value: null, matchMode: FilterMatchMode.EQUALS };
          break;
      }
      return;
    }
    
    // Filtre tipi haritasından bu alanın tipini ve matchMode'unu al
    const fieldConfig = filterTypeMap[key] || { type: 'text', matchMode: FilterMatchMode.STARTS_WITH };
    const filterType = typeof fieldConfig === 'object' ? fieldConfig.type : fieldConfig;
    const matchMode = typeof fieldConfig === 'object' ? fieldConfig.matchMode : 
      (filterType === 'text' ? FilterMatchMode.STARTS_WITH : FilterMatchMode.EQUALS);
    
    switch (filterType) {
      case 'autoComplete':
        _filters[key] = { value: value, matchMode: matchMode };
        break;
        
      case 'dropdown':
        _filters[key] = { value: value, matchMode: matchMode };
        break;
        
      case 'boolean':
        _filters[key] = { value: value, matchMode: matchMode };
        break;
        
      case 'number':
        _filters[key] = {
          operator: FilterOperator.AND,
          constraints: [{ value: value, matchMode: matchMode }]
        };
        break;
        
      case 'date': {
        // Ensure date values are properly converted to Date objects
        const dateValue = value instanceof Date ? value : new Date(value);
        _filters[key] = {
          operator: FilterOperator.AND,
          constraints: [{ value: dateValue, matchMode: matchMode }]
        };
        break;
      }
        
      case 'text':
      default:
        _filters[key] = {
          operator: FilterOperator.AND,
          constraints: [{ value: value, matchMode: matchMode }]
        };
        break;
    }
  });
  
  return _filters;
};

/**
 * DataTable filtreleri için başlangıç değerlerini oluşturur
 * 
 * @param {Array} fields - Filtre alanları dizisi
 * @param {Object} filterTypeMap - Filtre alanları ve tipleri/matchMode eşleştirmesi
 * @returns {Object} - Başlangıç filtresi nesnesi
 */
export const createInitialFilters = (fields = [], filterTypeMap = {}) => {
  const initialFilters = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS }
  };
  
  fields.forEach(field => {
    const fieldConfig = filterTypeMap[field] || { type: 'text', matchMode: FilterMatchMode.STARTS_WITH };
    const filterType = typeof fieldConfig === 'object' ? fieldConfig.type : fieldConfig;
    const matchMode = typeof fieldConfig === 'object' ? fieldConfig.matchMode : 
      (filterType === 'text' ? FilterMatchMode.STARTS_WITH : FilterMatchMode.EQUALS);
    
    switch (filterType) {
      case 'boolean':
        initialFilters[field] = { value: null, matchMode: matchMode };
        break;
        
      case 'autoComplete':
        initialFilters[field] = { value: null, matchMode: matchMode };
        break;
        
      case 'dropdown':
        initialFilters[field] = { value: null, matchMode: matchMode };
        break;
        
      case 'number':
      case 'date':
        initialFilters[field] = { 
          operator: FilterOperator.AND,
          constraints: [{ value: null, matchMode: matchMode }] 
        };
        break;
        
      case 'text':
      default:
        initialFilters[field] = { 
          operator: FilterOperator.AND,
          constraints: [{ value: null, matchMode: matchMode }] 
        };
        break;
    }
  });
  
  return initialFilters;
};

/**
 * Bir filtrenin aktif olup olmadığını kontrol eder
 * 
 * @param {Object} filter - Kontrol edilecek filtre nesnesi
 * @returns {boolean} - Filtre aktif ise true, değilse false
 */
export const hasFilter = (filter) => {
  if (!filter) return false;
  
  // Basit filtre kontrolü (boolean, autoComplete, dropdown vb.)
  if (filter.value !== undefined && filter.value !== null) {
    return true;
  }
  
  // Karmaşık filtre kontrolü (text, number, date vb.)
  if (filter.constraints && Array.isArray(filter.constraints)) {
    // Constraints dizisinin en az bir elemanı olduğunu ve
    // ilk elemanın değerinin null olmadığını kontrol et
    return filter.constraints.length > 0 && 
           filter.constraints[0] && 
           filter.constraints[0].value !== null && 
           filter.constraints[0].value !== undefined;
  }
  
  return false;
};
